import { getCorrectAnswerIds } from "@helpers/session";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import type { Answer, Answers, ItemState, ItemStates } from "../hooks/types";

// Rebuilds local answer state from the per-item answers echoed by
// /sessions/{id}/items, so a session opened on a fresh device scores and
// renders like the device it was taken on. Tests stay editable (selection only,
// no per-item submit); module items render answered + revealed.
export const seedAnswerStateFromItems = (
	sessionItems: BaseItemSchema[],
	isSummativeTest: boolean,
): { itemStates: ItemStates; answers: Answers } => {
	const itemStates: ItemStates = new Map<number, ItemState>();
	const answers: Answers = new Map<number, Answer>();
	for (const item of sessionItems) {
		const embedded = item.answer;
		if (item.id == null || !embedded?.selectedChoiceIds?.length) {
			continue;
		}
		if (isSummativeTest) {
			itemStates.set(item.id, {
				selectedChoiceIds: embedded.selectedChoiceIds,
				scores: [],
			});
			continue;
		}
		const correct = embedded.correct ?? (embedded.score ?? 0) > 0;
		itemStates.set(item.id, {
			selectedChoiceIds: embedded.selectedChoiceIds,
			correctAnswerIds: getCorrectAnswerIds(item),
			scores: [embedded.score ?? (correct ? 100 : 0)],
			answered: true,
		});
		answers.set(item.id, {
			correct,
			confidence: embedded.confidence as Answer["confidence"],
			timeTaken: embedded.timeTaken,
			selectedChoiceIds: embedded.selectedChoiceIds,
		});
	}
	return { itemStates, answers };
};

// Locally persisted selections win (they can lead the answer echo right after
// finishing); a fresh device seeds from the echo.
export const resolveSummativeItemStates = (
	sessionItems: BaseItemSchema[],
	localItemStates: ItemStates | undefined,
): ItemStates =>
	localItemStates?.size
		? localItemStates
		: seedAnswerStateFromItems(sessionItems, true).itemStates;

// Cross-device resume position: the echo flags the item of the most recent
// answer-event as `lastUpdated`. Resume lands after it when its answer echoed,
// or on it when the echo is missing (the user still needs to answer it).
export const deriveResumeIndexFromAnswerEcho = (
	sessionItems: BaseItemSchema[],
): number => {
	const lastUpdatedIndex = sessionItems.findIndex((item) => item.lastUpdated);
	if (lastUpdatedIndex < 0) {
		return 0;
	}
	const answered =
		sessionItems[lastUpdatedIndex].answer?.selectedChoiceIds?.length;
	const resumeIndex = answered ? lastUpdatedIndex + 1 : lastUpdatedIndex;
	return Math.min(resumeIndex, sessionItems.length - 1);
};
